import React, {useEffect} from 'react';
import clsx from 'clsx';

import {styled, composeClasses} from '@netx/core-components/Theme';
import tabPanelClasses, {
	getTabPanelClass,
	TabPanelClasses,
} from './tabPanelClasses';

const useUtilityClasses = (ownerState: any) => {
	const {classes, active, disablePadding} = ownerState;
	const slots = {
		root: ['root', active && 'active', !disablePadding && 'pad'],
	};
	return composeClasses(slots, getTabPanelClass, classes);
};

const TabPanelRoot = styled('div', {
	name: 'TabPanel',
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	minHeight: 0,
	overflow: 'hidden',
	[`&:not(.${tabPanelClasses.active})`]: {
		display: 'none',
	},
	[`&.${tabPanelClasses.active}.${tabPanelClasses.pad}`]: {
		padding: theme.spacing(2, 0, 0),
	},
}));

export interface TabPanelProps
	extends React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>> {
	classes?: TabPanelClasses;
	disablePadding?: boolean;
	keepMounted?: boolean;
	keepMountedAfterActive?: boolean;
	index: number;
	value: number;
}

/**
 * A wrapper for tab content\
 * To be used in conjunction with `useTabs`
 * @example
   const tabs = useTabs(options);
   return (
      <>
         <Tabs
            onChange={tabs.handleChange}
            value={tabs.activeTabIndex}
        >
            <Tab label="tab 1" />
            <Tab label="tab 2" />
         </Tabs>
         <TabPanel value={tabs.activeTabIndex} index={0}>...</TabPanel>
         <TabPanel value={tabs.activeTabIndex} index={1}>...</TabPanel>
      </>
   );
 */
const TabPanelPrimitive = (props: TabPanelProps) => {
	const {
		children,
		className,
		disablePadding,
		index,
		keepMounted,
		keepMountedAfterActive,
		value,
		...sendProps
	} = props;

	const active = value === index;

	const ownerState = {
		...props,
		active,
	};
	const classes = useUtilityClasses(ownerState);

	const [isMounted, setIsMounted] = React.useState(active);
	useEffect(() => {
		if (active) setIsMounted(true);
	}, [active]);

	return (
		<TabPanelRoot
			role="tabpanel"
			hidden={!active}
			id={`tabpanel-${index}`}
			aria-labelledby={`tab-${index}`}
			/** Adding `multiple-tabs` to get around a global style in our app for non react tabs */
			className={clsx(classes.root, 'multiple-tabs', className)}
			{...sendProps}
		>
			{(isMounted && keepMountedAfterActive) || keepMounted || active
				? children
				: null}
		</TabPanelRoot>
	);
};

export const TabPanel = React.memo(TabPanelPrimitive);
export default TabPanel;
